// Popup menu (button + list of actions) with keyboard support; opens upwards near the screen bottom.
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export interface MenuItem {
  label: string;
  icon?: IconName;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Draws a separator above this item. */
  separator?: boolean;
}

interface MenuProps {
  items: MenuItem[];
  /** Accessible name of the trigger button. */
  label: string;
  /** Custom trigger content; defaults to a "more" icon button. */
  trigger?: ReactNode;
  triggerClassName?: string;
  align?: 'start' | 'end';
  /** Optional heading shown at the top of the list. */
  heading?: string;
}

let openMenus = 0;

/** True while a popup menu is open (keyboard shortcuts should then stay inactive). */
export function isMenuOpen(): boolean {
  return openMenus > 0;
}

export function Menu({ items, label, trigger, triggerClassName, align = 'end', heading }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const listHeight = listRef.current?.offsetHeight ?? 240;
    const below = window.innerHeight - rect.bottom;
    setUp(below < listHeight + 96 && rect.top > below);
    listRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    openMenus++;
    const onPointerDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      openMenus = Math.max(0, openMenus - 1);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  };

  const onListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const buttons = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])') ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        close(true);
        break;
      case 'ArrowDown':
        e.preventDefault();
        buttons[(index + 1) % buttons.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        buttons[(index - 1 + buttons.length) % buttons.length]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        buttons[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        buttons[buttons.length - 1]?.focus();
        break;
      case 'Tab':
        close(false);
        break;
    }
  };

  return (
    <div className="menu" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={triggerClassName ?? 'icon-btn'}
        aria-label={label}
        title={trigger ? undefined : label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger ?? <Icon name="more" />}
      </button>
      {open && (
        <div
          ref={listRef}
          id={menuId}
          className={`menu__list menu__list--${align}${up ? ' menu__list--up' : ''}`}
          role="menu"
          aria-label={label}
          onKeyDown={onListKeyDown}
        >
          {heading && <div className="menu__heading">{heading}</div>}
          {items.map((item, i) => (
            <button
              key={`${item.label}-${i}`}
              type="button"
              role="menuitem"
              className={`menu__item${item.danger ? ' menu__item--danger' : ''}${item.separator ? ' menu__item--separated' : ''}`}
              disabled={item.disabled}
              onClick={() => {
                close(true);
                item.onSelect();
              }}
            >
              {item.icon && <Icon name={item.icon} size={20} />}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
